import LinkIcon from '@mui/icons-material/OpenInNew'
import SyncIcon from '@mui/icons-material/Sync'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useCallback, useEffect, useState } from 'react'

import { COMMON, RGM_VIEW as L } from '../labels'
import { api } from '../lib/api'
import { relativeDays, shortDate } from '../lib/format'
import type { BacklogState, BacklogTask } from '../lib/types'
import { FONT_MONO, INK, INK_FAINT, INK_SOFT, STATUS_COLOR, monoLabel, serifNumber } from '../theme'

/** El grupo de status de Notion, pintado con los pigmentos que ya usa el tablero. */
const GROUP_COLOR: Record<string, string> = {
  done: STATUS_COLOR.validated,
  in_progress: STATUS_COLOR.available,
  todo: STATUS_COLOR.upcoming,
}

/** Lo pendiente primero; lo cerrado al final, donde deja de pedir atención. */
const GROUP_ORDER: Record<string, number> = { in_progress: 0, todo: 1, done: 2 }

type Toast = { msg: string; severity: 'success' | 'warning' | 'error' }

/**
 * Lee el resultado del callback de Notion (`?notion=connected|denied|error`) y
 * limpia la URL, para que recargar la página no repita el aviso.
 */
function takeOAuthResult(): Toast | null {
  const params = new URLSearchParams(window.location.search)
  const result = params.get('notion')
  if (!result) return null
  const detail = params.get('detail')
  window.history.replaceState(null, '', window.location.pathname)
  if (result === 'connected') return { msg: L.connectedToast(detail), severity: 'success' }
  if (result === 'denied') return { msg: L.deniedToast, severity: 'warning' }
  return { msg: L.errorToast(detail), severity: 'error' }
}

/**
 * El Backlog RGM tal como quedó en la última sincronización.
 *
 * Carga sus datos por su cuenta y no dentro del `load` de App: si Notion o el
 * almacén fallan, el resto del tablero tiene que seguir funcionando.
 */
export function BacklogRgmView({ today }: { today: string }) {
  const [state, setState] = useState<BacklogState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [busy, setBusy] = useState<'sync' | 'connect' | null>(null)
  const [toast, setToast] = useState<Toast | null>(() => takeOAuthResult())

  const load = useCallback(async () => {
    try {
      setState(await api.backlog())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : COMMON.error)
    }
  }, [])

  useEffect(() => {
    void load()
    api
      .me()
      .then((me) => setIsAdmin(me.role === 'admin'))
      .catch(() => setIsAdmin(false))
  }, [load])

  async function sync() {
    setBusy('sync')
    try {
      const r = await api.syncBacklog()
      setToast(r.error ? { msg: r.error, severity: 'error' } : { msg: L.syncDone, severity: 'success' })
      await load()
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : COMMON.error, severity: 'error' })
    } finally {
      setBusy(null)
    }
  }

  async function connect() {
    setBusy('connect')
    try {
      const { url } = await api.authorizeNotion()
      // Navegación completa: Notion regresa al callback y de ahí a esta vista.
      window.location.assign(url)
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : COMMON.error, severity: 'error' })
      setBusy(null)
    }
  }

  if (error) return <Alert severity="error">{error}</Alert>
  if (!state) return <LinearProgress sx={{ width: 180, mx: 'auto', mt: 8 }} />

  const tasks = [...(state.backlog?.tasks ?? [])].sort(
    (a, b) =>
      (GROUP_ORDER[a.status_group] ?? 9) - (GROUP_ORDER[b.status_group] ?? 9) ||
      (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999'),
  )
  const conn = state.connection

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        spacing={2}
      >
        <Box>
          <Typography sx={monoLabel}>{L.eyebrow}</Typography>
          <Typography sx={{ ...serifNumber, fontSize: '2.25rem', mt: 0.5 }}>{L.title}</Typography>
          <Typography sx={{ color: INK_SOFT, mt: 0.75, maxWidth: 640 }}>{L.subtitle}</Typography>
        </Box>
        {isAdmin && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => void connect()}
              disabled={busy !== null || !conn.oauth_configured}
            >
              {conn.mode === 'oauth' ? L.reconnect : L.connect}
            </Button>
            <Button
              variant="contained"
              startIcon={busy === 'sync' ? <CircularProgress size={14} /> : <SyncIcon />}
              onClick={() => void sync()}
              disabled={busy !== null || !conn.connected}
            >
              {busy === 'sync' ? L.syncing : L.syncNow}
            </Button>
          </Stack>
        )}
      </Stack>

      <SyncStatus state={state} today={today} />

      <Paper sx={{ overflowX: 'auto' }}>
        {tasks.length === 0 ? (
          <Typography sx={{ p: 4, color: INK_SOFT }}>{L.empty}</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{L.colTask}</TableCell>
                <TableCell>{L.colStatus}</TableCell>
                <TableCell>{L.colOwner}</TableCell>
                <TableCell>{L.colPriority}</TableCell>
                <TableCell>{L.colDue}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Snackbar
        open={toast !== null}
        autoHideDuration={8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  )
}

/** Conexión, última sincronización y el resultado del espejo en ADO. */
function SyncStatus({ state, today }: { state: BacklogState; today: string }) {
  const conn = state.connection
  const ado = state.ado
  const syncedDay = state.synced_at?.slice(0, 10) ?? null

  return (
    <Stack spacing={1}>
      {!conn.oauth_configured && conn.mode === 'none' && <Alert severity="warning">{L.oauthMissing}</Alert>}
      {conn.oauth_configured && !conn.connected && <Alert severity="warning">{L.notConnected}</Alert>}
      {state.error && (
        <Alert severity="error">
          {L.lastAttemptFailed}: {state.error}
        </Alert>
      )}
      {ado?.enabled && ado.error && (
        <Alert severity="error">
          {L.ado}: {ado.error}
        </Alert>
      )}
      {ado?.notice && <Alert severity="warning">{ado.notice}</Alert>}

      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Meta
          label={L.syncedAt}
          value={
            state.synced_at
              ? L.syncedValue(
                  shortDate(syncedDay),
                  state.synced_at.slice(11, 16),
                  relativeDays(syncedDay, today),
                )
              : L.neverSynced
          }
        />
        {conn.connected && <Meta label={L.notion} value={L.connectedTo(conn.workspace)} />}
        <Meta
          label={L.ado}
          value={
            !ado || !ado.enabled
              ? L.adoOff(ado?.reason ?? null)
              : ado.error
                ? COMMON.none
                : L.adoCounts(ado.created ?? 0, ado.updated ?? 0, ado.unchanged ?? 0, ado.removed ?? 0)
          }
        />
        {(ado?.errors?.length ?? 0) > 0 && (
          <Tooltip title={ado!.errors!.join('\n')}>
            <Box>
              <Meta label={L.ado} value={L.adoErrors(ado!.errors!.length)} />
            </Box>
          </Tooltip>
        )}
        <Meta label={L.colTask} value={L.count(state.backlog?.tasks.length ?? 0)} />
      </Stack>
    </Stack>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography sx={monoLabel}>{label}</Typography>
      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.75rem', color: INK }}>{value}</Typography>
    </Box>
  )
}

function TaskRow({ task }: { task: BacklogTask }) {
  const color = GROUP_COLOR[task.status_group] ?? INK_FAINT
  const done = task.status_group === 'done'
  return (
    <TableRow hover>
      <TableCell sx={{ color: done ? INK_FAINT : INK, maxWidth: 520 }}>{task.title}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{task.status}</Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ color: INK_SOFT }}>{task.owner ?? COMMON.none}</TableCell>
      <TableCell sx={{ color: INK_SOFT }}>{task.priority ?? COMMON.none}</TableCell>
      <TableCell sx={{ fontFamily: FONT_MONO, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
        {shortDate(task.due_date)}
      </TableCell>
      <TableCell padding="checkbox">
        {task.url && (
          <Tooltip title={L.openInNotion}>
            <IconButton size="small" href={task.url} target="_blank" rel="noreferrer">
              <LinkIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  )
}
